import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MessageCircle, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1606577924006-27d39b132ae2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const WHATSAPP_URL = "https://wa.me/447348371266";

const LINES = ["More Power", "Better Drive", "Better Economy"];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};
const lineVariant = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayFade = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      {/* Background image + parallax */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
        <img src={HERO_IMG} alt="Performance car engine bay under service" className="h-full w-full object-cover" />
      </motion.div>

      {/* Overlays */}
      <motion.div style={{ opacity: overlayFade }} className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-brand-charcoal" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_15%,rgba(230,0,0,0.28),transparent_45%)]" />
      <div className="absolute inset-0 grain opacity-[0.06] mix-blend-overlay pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 min-h-[100svh] flex flex-col justify-center pt-24 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="hero-eyebrow"
          className="flex items-center gap-3 text-brand-red font-semibold uppercase tracking-[0.35em] text-xs sm:text-sm mb-7"
        >
          <span className="h-px w-10 bg-brand-red" />
          Performance • Diagnostics • Results
        </motion.p>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          data-testid="hero-slogan"
          className="font-display italic font-extrabold uppercase tracking-tight text-white leading-[0.86] text-6xl sm:text-7xl md:text-8xl lg:text-9xl"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span variants={lineVariant} className={`block ${i === 2 ? "text-brand-red text-glow-red" : ""}`}>
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-8 max-w-xl text-brand-offwhite/80 text-base md:text-lg leading-relaxed"
        >
          Castleford ECU Remaps &amp; Diagnostics — Stage 1, 2 &amp; 3 ECU remapping, performance &amp; economy tuning and
          full vehicle diagnostics, brought right to your door.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-11 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-message-btn"
            className="group inline-flex items-center justify-center gap-2.5 bg-brand-red hover:bg-brand-redhover text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-sm box-glow-red transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
          >
            <MessageCircle size={18} strokeWidth={2.4} />
            Message Us
          </a>
          <a
            href="#services"
            data-testid="hero-services-btn"
            className="inline-flex items-center justify-center gap-2.5 border border-white/25 hover:border-brand-red text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-sm transition-colors duration-200"
          >
            View Services
          </a>
        </motion.div>
      </div>

      <motion.a
        href="#services"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 text-brand-grey hover:text-white transition-colors"
        aria-label="Scroll to services"
      >
        <ArrowDown size={22} className="animate-bounce" />
      </motion.a>
    </section>
  );
};
